import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import { agents, landing, toneSurface } from "@/lib/mockData";
import { cn } from "@/lib/utils";

export default function AgentRoster() {
  return (
    <section
      data-testid="agent-roster"
      id="agents"
      className="px-5 py-24 md:px-8 md:py-32"
    >
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5 }}
          className="mb-12 flex flex-col items-start justify-between gap-3 md:flex-row md:items-end"
        >
          <div>
            <div className="eyebrow">Meet your executive team</div>
            <h2 className="mt-3 text-[40px] font-black leading-[1.02] tracking-tight text-zinc-900 md:text-[64px]">
              Eight agents.<br />Endless leverage.
            </h2>
          </div>
          <p className="max-w-md text-[14px] leading-relaxed text-zinc-600">
            Each agent owns a domain, learns your company's context, and shows
            up to every boardroom debate with evidence-backed positions.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.04 } },
          }}
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          {agents.map((a) => {
            const tone = toneSurface[a.tone] ?? toneSurface.neutral;
            const isDark = a.tone === "dark";
            const tagline = landing.agentTaglines[a.id];
            return (
              <motion.div
                key={a.id}
                data-testid={`roster-agent-${a.id}`}
                variants={{
                  hidden: { opacity: 0, y: 18 },
                  show: {
                    opacity: 1,
                    y: 0,
                    transition: { type: "spring", stiffness: 120, damping: 18 },
                  },
                }}
                whileHover={{ y: -4 }}
                className={cn(
                  "group relative flex flex-col gap-3 overflow-hidden rounded-[24px] border p-5 transition-shadow duration-300 hover:shadow-[0_24px_48px_-24px_rgba(15,15,25,0.18)]",
                  tone.bg,
                  tone.text,
                  tone.border,
                )}
              >
                <div className="flex items-center justify-between">
                  <div
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-full text-[13px] font-extrabold",
                      isDark
                        ? "bg-white/15 text-white"
                        : "bg-white/80 text-zinc-900",
                    )}
                  >
                    {a.name[0]}
                  </div>
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em]",
                      isDark ? "bg-white/10 text-white/80" : "bg-white/70 text-zinc-700",
                    )}
                  >
                    {a.role}
                  </span>
                </div>
                <div>
                  <div className="text-[24px] font-extrabold leading-none tracking-tight">
                    {a.name}
                  </div>
                  <div
                    className={cn(
                      "mt-1.5 text-[11.5px]",
                      isDark ? "text-white/55" : "text-zinc-500",
                    )}
                  >
                    {a.focus}
                  </div>
                </div>
                <p
                  className={cn(
                    "mt-1 text-[12.5px] leading-relaxed",
                    isDark ? "text-white/80" : "text-zinc-700",
                  )}
                >
                  {tagline}
                </p>
              </motion.div>
            );
          })}

          {/* 9th: Build your own */}
          <Link
            to="/settings"
            data-testid="roster-build-your-own"
            className="group flex aspect-auto flex-col items-center justify-center gap-3 rounded-[24px] border-2 border-dashed border-black/[0.10] bg-white/40 p-5 text-center transition-colors hover:border-violet-400 hover:bg-white/80"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 text-zinc-700 transition-colors group-hover:bg-violet-100 group-hover:text-violet-700">
              <Plus className="h-5 w-5" strokeWidth={2.4} />
            </div>
            <div className="text-[16px] font-extrabold tracking-tight text-zinc-900">
              Build your own agent
            </div>
            <p className="text-[12px] leading-relaxed text-zinc-500">
              Spin up custom personas from a Settings template. Coming soon.
            </p>
            <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-zinc-700">
              Configure
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.4} />
            </span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
